import React, {useState, useMemo, useCallback, memo, useContext, useEffect} from "react";
import {useTranslation} from "react-i18next";
//local
import {Info} from "components/Info";
import {Input} from "components/Input";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {SignContractEditButton} from "./atoms/SignContractEditButton";
import {
  signContractMemberStatus,
  signContractMemberStatusName,
  signContractMemberStatusColor,
  signContractSide,
  signContractPartySide,
  signContractPartyMemberSide,
  signContractIdentifyMethod,
  signContractIdentifyMethodOptions,
  signContractAuthenticateStatus,
} from "../constants";
import {remindSignContractMember} from "../api/contracts";
import ButtonDelete from "components/buttons/ButtonDelete";
import MiniSelect from "components/selects/MiniSelect";
import MultipleSelect from "components/selects/MultipleSelect";
import {createArray, getFullName} from "helpers/helper";
import {dateTimeFormat} from "helpers/datetime";
import {Tag} from "components/Tag";
import {CustomTooltip} from "components/CustomTooltip";
import {SignContractCompanyInfoTag} from "./atoms/SignContractCompanyInfoTag";
import {SignContractAuthenticatedVerificationIcon} from "./atoms/SignContractAuthenticatedVerificationIcon";
import {SignContractIdentifyMethodIconDisplay} from "./atoms/SignContractIdentifyMethodIconDisplay";
import SignContractInviteLink from "./atoms/SignContractInviteLink";
import {formatCompanyNumber} from "helpers/helper";
import {ConfirmModal} from "components/ConfirmModal";
import {SignContractContext} from "../SignContractContext";
import {LockValue} from "components/LockValue";
import {hasPermission, updateLocationHash} from "helpers/helper";
import {
  IconPencilLine,
  IconPlane,
  IconCrown,
  IconPlus,
  IconSMSVerification,
  IconBankIDVerification,
  IconHourglass,
} from "components/icons";
import {hashes, subscriptionSubTabHash} from "constants/tabs.const";
import {SignContractIdentificationDetailsModal} from "./SignContractIdentificationDetailsModal";
import cn from "classnames";
import {color} from "constants/color.consts";

const baseClass = "signing_contract__data__party";
const baseLangKey = "SIGN_CONTRACT_DATA_SIGNING_PARTIES_";

export const SignContractSigningParty = memo(
  ({contract, partySide, isSignOrderUsed, addClass, onChange}) => {
    const {t} = useTranslation();
    const {user, organizationMembers, side, status, isPublic, setEdits} =
      useContext(SignContractContext);

    const isBuyerSide = side === signContractSide.BUYER;
    const isSellerSide = side === signContractSide.SELLER;
    const isOwnSide = partySide === signContractPartySide.OWN;
    const isTheirSide = partySide === signContractPartySide.THEIR;

    const isExtraSignIdentify = hasPermission([
      "ENABLE_SIGN_CONTRACT_EXTRA_SIGN_IDENTIFY",
    ]);
    const canUseHandysignSigningOrder = hasPermission([
      "ENABLE_SIGN_CONTRACT_SIGNING_ORDER",
    ]);
    const showSignOrderSelect =
      status.isDraft && canUseHandysignSigningOrder && (isOwnSide || isTheirSide);

    const identifyMethodListForParticipant = useCallback(
      (participant) => {
        if (isExtraSignIdentify) {
          return signContractIdentifyMethodOptions;
        }
        if (isTheirSide) {
          const current = participant.identifyMethod;
          const options = signContractIdentifyMethodOptions.slice(0, -1);
          if (!options.some((o) => o.value === current)) {
            const fallback = signContractIdentifyMethodOptions.find(
              (o) => o.value === current
            );
            return fallback ? [...options, fallback] : options;
          }
          return options;
        }
        const current = participant.identifyMethod;
        const matching = signContractIdentifyMethodOptions.filter(
          (o) => o.value === current
        );
        if (matching.length) return matching;
        const fallback = signContractIdentifyMethodOptions.find(
          (o) => o.value === current
        );
        return fallback ? [fallback] : matching;
      },
      [isExtraSignIdentify, isTheirSide]
    );
    const isMyBuyerSide = isBuyerSide && isOwnSide;
    const isMySellerSide = isSellerSide && isTheirSide;
    const titleLabel = isOwnSide
      ? `${baseLangKey}BUYER_TITLE`
      : `${baseLangKey}SELLER_TITLE`;

    const [companyInfo, setCompanyInfo] = useState({
      companyNumber: contract?.companyNumber,
      companyName: contract?.companyName,
    });
    const initiatingPartyOrganizationMemberIds = useMemo(
      () => contract.initiatingMembers.map((item) => item.OrganizationMemberId),
      [contract.initiatingMembers]
    );
    const wholeParticipants = useMemo(
      () => [...contract.initiatingMembers, ...contract.counterpartyMembers],
      [contract]
    );
    const activeSignOrder = useMemo(
      () =>
        wholeParticipants
          .filter((p) => !p.isSigned)
          .reduce((min, p) => (p.signOrder < min ? p.signOrder : min), Infinity),
      [wholeParticipants]
    );

    const participants = useMemo(
      () => contract?.[signContractPartyMemberSide[partySide]] || [],
      [contract, partySide]
    );
    const hasParticipants = useMemo(() => participants?.length > 0, [participants]);
    const showSignOrderLabel = showSignOrderSelect && hasParticipants;
    const participantsOrganizationMemberIds = useMemo(
      () => participants.map((p) => p.OrganizationMemberId),
      [participants]
    );
    const isLastSigningParticipant = useMemo(
      () =>
        !isPublic &&
        participants?.filter(
          (item) => item.OrganizationId === user.ActiveOrganizationId && !item.isSigned
        ).length === 1,
      [participants, user, isPublic]
    );
    const hasSigningParticipants = useMemo(
      () => participants?.filter((item) => !item.isSigned).length > 0,
      [participants]
    );
    const hasMoreThanOneMyOrganizationSigningParticipants = useMemo(
      () =>
        participants?.filter(
          (item) => !item.isSigned && item.OrganizationId === user?.ActiveOrganizationId
        ).length > 1,
      [participants, user]
    );
    const isAddOwnSideButton = useMemo(
      () =>
        isMyBuyerSide && (status.isDraft || (status.isSigning && hasSigningParticipants)),
      [isMyBuyerSide, status, hasSigningParticipants]
    );
    const isOwnSideBuyerSideRightTitleContent = useMemo(
      () => isOwnSide && isBuyerSide && status.isDraft,
      [isOwnSide, isBuyerSide, status]
    );
    const isAddTheirSideBuyerSideButton = useMemo(
      () => isTheirSide && isBuyerSide && status.isDraft,
      [isTheirSide, isBuyerSide, status]
    );
    const isAddTheirSideSellerSideButton = useMemo(
      () => isMySellerSide && !isPublic && status.isSigning,
      [isMySellerSide, status, isPublic]
    );

    const addableOrganizationMembers = useMemo(
      () =>
        organizationMembers
          .map((orgMember) => ({
            value: orgMember.id,
            label: getFullName(orgMember.User.firstName, orgMember.User.lastName),
          }))
          .filter((item) => !participantsOrganizationMemberIds.includes(item.value)),
      [organizationMembers, participantsOrganizationMemberIds]
    );
    const hasAddableOrganizationMembers = useMemo(
      () => addableOrganizationMembers?.length > 0,
      [addableOrganizationMembers]
    );
    const [isEditCompany, setIsEditCompany] = useState(null);
    const [identificationDetailsModal, setIdentificationDetailsModal] = useState(null);
    const openIdentificationDetails = useCallback((participant, methodKind) => {
      setIdentificationDetailsModal({participant, methodKind});
    }, []);

    const AddTheirPartyMemberButton = () => (
      <ButtonTransparent
        name={`${baseLangKey}SELLER_SELECTION`}
        onClick={() => onChange(signContractPartyMemberSide[partySide], undefined, "add")}
        icon={<IconPlus />}
        small
        rounded
        disabled={false}
        addClass={`pl-1 ${hasParticipants ? "mt-4 mb-2" : "bg-yellow ml-4"}`}
      />
    );

    const RightTitleContent = () => (
      <React.Fragment>
        {(showSignOrderLabel || isExtraSignIdentify) && (
          <div className="df-row-center __uppercase fz-12 __bold discrete-text mla">
            {showSignOrderLabel && (
              <span className="mr-6">
                {t("SIGN_CONTRACT_DATA_SIGNING_PARTIES_TABLE_HEAD_SIGN_ORDER")}
              </span>
            )}
            {isExtraSignIdentify && (
              <span style={{width: "105px"}}>
                {t("SIGN_CONTRACT_DATA_SIGNING_PARTIES_TABLE_HEAD_IDENTIFY_BY")}
              </span>
            )}
          </div>
        )}
        {status.isDraft && !isExtraSignIdentify && (
          <div className="df-row-center mla fz-12">
            <span>
              {t("SIGN_CONTRACT_DATA_SIGNING_PARTIES_TABLE_HEAD_NO_EXTRA_SIGN_IDENTIFY")}
            </span>
            <ButtonTransparent
              name="SIGN_CONTRACT_DATA_SIGNING_PARTIES_BUTTON_NO_EXTRA_SIGN_IDENTIFY"
              small
              addClass="ml-2"
              onClick={() => {
                const targetHash = `${hashes.SUBSCRIPTION}_${subscriptionSubTabHash.DIGITAL_SIGNING}`;
                updateLocationHash(targetHash);
              }}
            />
          </div>
        )}
      </React.Fragment>
    );
    const handleDeleteContractParticipant = (participant) =>
      ConfirmModal({
        title: t("SIGN_CONTRACT_MEMBER_DELETE_MODAL_TITLE"),
        text: t("SIGN_CONTRACT_MEMBER_DELETE_MODAL_TEXT").format(
          getFullName(participant?.firstName, participant?.lastName)
        ),
        type: "warning",
        noQuestionSymbol: true,
        onOk: () =>
          onChange(signContractPartyMemberSide[partySide], participant.id, "remove"),
      });

    // ====================================== ParticipantStatusInfo ======================================
    const ParticipantStatusInfo = memo(({participant, onOpenIdentificationDetails}) => {
      const isQueued = useMemo(
        () => !participant.isSigned && (participant.signOrder || 1) > activeSignOrder,
        [participant.isSigned, participant.signOrder]
      );

      const participantStatus = useMemo(() => {
        if (participant.isSigned) return signContractMemberStatus.SIGNED;
        if (isQueued) return signContractMemberStatus.QUEUED;
        return participant.notifyStatus;
      }, [participant, isQueued]);
      const isTooltip = useMemo(
        () =>
          [signContractMemberStatus.BOUNCED, signContractMemberStatus.REMINDED].includes(
            participantStatus
          ),
        [participantStatus]
      );
      const remindedMember = participant.RemindedMember?.User;
      const tooltip = {
        [signContractMemberStatus.BOUNCED]: t(`${baseLangKey}MEMBER_STATUS_BOUNCED`),
        [signContractMemberStatus.REMINDED]: t(
          `${baseLangKey}MEMBER_STATUS_REMINDED`
        ).format(
          remindedMember
            ? getFullName(remindedMember.firstName, remindedMember.lastName)
            : t(`${baseLangKey}MEMBER_STATUS_REMINDED_MEMBER`)
        ),
      };

      const isSmsAuthenticated =
        participant.isSigned &&
        participant.authenticateStatus ===
          signContractAuthenticateStatus.SMS_AUTHENTICATED;
      const isBankIdAuthenticated =
        participant.isSigned &&
        participant.authenticateStatus ===
          signContractAuthenticateStatus.BANKID_AUTHENTICATED;

      const openSmsIdentification = () =>
        onOpenIdentificationDetails(participant, signContractIdentifyMethod.SMS);
      const openBankIdIdentification = () =>
        onOpenIdentificationDetails(participant, signContractIdentifyMethod.BANKID);

      const timeAndDate = dateTimeFormat(
        participant.isSigned ? participant.signedAt : participant.notifyUpdatedAt
      );

      return (
        <React.Fragment>
          {!participant.isSigned &&
            (isQueued ? (
              <IconHourglass
                width="16"
                height="15"
                addClass="mr-1 fs0"
                color={color.black}
              />
            ) : (
              <IconPlane width="16" height="15" addClass="mr-1 fs0" />
            ))}
          {isSmsAuthenticated && (
            <SignContractAuthenticatedVerificationIcon
              onOpen={openSmsIdentification}
              tooltipKey="SIGN_CONTRACT_SMS_AUTHENTICATED_TOOLTIP"
              Icon={IconSMSVerification}
            />
          )}
          {isBankIdAuthenticated && (
            <SignContractAuthenticatedVerificationIcon
              onOpen={openBankIdIdentification}
              Icon={IconBankIDVerification}
            />
          )}
          <CustomTooltip text={timeAndDate}>
            <div>
              <Tag
                text={signContractMemberStatusName[participantStatus]}
                color={signContractMemberStatusColor[participantStatus]}
                noBackground
                uppercase
                bold
                addClass="fz-10 mr-0"
                needTranslation
              />
            </div>
          </CustomTooltip>
          {isTooltip && (
            <Info tooltip={tooltip[participantStatus]} noTranslate addClass="ml-1" />
          )}
          {!isQueued && (
            <span className="signing_contract__data__party-time ml-2 ws-nowrap">
              {timeAndDate}
            </span>
          )}
        </React.Fragment>
      );
    });

    // ====================================== ParticipantActions ======================================
    const ParticipantActions = memo(({participant, isCurrentUser}) => {
      const isQueued = useMemo(
        () => !participant.isSigned && (participant.signOrder || 1) > activeSignOrder,
        [participant.isSigned, participant.signOrder]
      );

      const participantStatus = useMemo(
        () =>
          participant.isSigned
            ? signContractMemberStatus.SIGNED
            : participant.notifyStatus,
        [participant]
      );
      const isRecentlyNotified = useMemo(
        () =>
          participantStatus === signContractMemberStatus.REMINDED &&
          Date.parse(participant.notifyUpdatedAt) > Date.now() - 86400000,
        [participant, participantStatus]
      );
      const isRemindButton = useMemo(
        () =>
          !isPublic &&
          !(
            participantStatus === signContractMemberStatus.BOUNCED || participant.isSigned
          ) &&
          !isQueued &&
          !isRecentlyNotified &&
          !(isOwnSide && isSellerSide),
        [participant, isRecentlyNotified, participantStatus, isQueued]
      );

      return (
        <React.Fragment>
          {isRemindButton && !isCurrentUser && (
            <ButtonTransparent
              name={`${baseLangKey}BUTTON_REMIND`}
              small
              icon={<IconPlane />}
              addClass="ml-2"
              onClick={() =>
                remindSignContractMember(contract.id, participant.id).then(() =>
                  onChange(undefined, undefined, "refresh")
                )
              }
            />
          )}
          {status.isSigning && isCurrentUser && !participant.signedAt && (
            <ButtonTransparent
              name={
                isQueued
                  ? `${baseLangKey}BUTTON_SIGN_QUEUED`
                  : `${baseLangKey}BUTTON_SIGN`
              }
              color={isQueued ? "mid-gray" : "blue"}
              small
              nonTransparent
              icon={
                isQueued ? <IconHourglass width="16" height="15" /> : <IconPencilLine />
              }
              addClass={`ml-2 ${isQueued ? "cnormal" : ""}`}
              onClick={() =>
                !isQueued &&
                onChange("memberSign", {
                  participantId: participant.id,
                  isLastSigningMember: isLastSigningParticipant,
                  identifyMethod: participant.identifyMethod,
                })
              }
            />
          )}
        </React.Fragment>
      );
    });

    // ====================================== ParticipantRow ======================================
    const ParticipantRow = memo(
      ({
        participant,
        identifyMethodListForParticipant: identifyListForParticipant,
        onOpenIdentificationDetails,
      }) => {
        const isCurrentUser = useMemo(
          () =>
            participant?.OrganizationMemberId
              ? participant?.OrganizationMemberId === user?.OrganizationMemberId
              : participant?.id === user?.id,
          [participant]
        );
        const isFromMyOrganization = useMemo(
          () => user?.ActiveOrganizationId === participant.OrganizationId,
          [participant.OrganizationId]
        );
        const isOwner = useMemo(
          () => contract?.createdMemberId === participant?.OrganizationMemberId,
          [participant]
        );
        const isSigned = useMemo(() => !!participant?.isSigned, [participant]);
        const sideCommonDeleteTerms = useMemo(() => !isSigned && !isPublic, [isSigned]);
        const sellerSideDeleteTerms = useMemo(
          () =>
            isFromMyOrganization &&
            sideCommonDeleteTerms &&
            hasMoreThanOneMyOrganizationSigningParticipants &&
            !initiatingPartyOrganizationMemberIds.includes(participant.createdMemberId),
          [isFromMyOrganization, sideCommonDeleteTerms, participant]
        );
        const isDeleteButton = useMemo(
          () =>
            !isCurrentUser &&
            !isOwner &&
            (status.isDraft ||
              (status.isSigning
                ? isMyBuyerSide
                  ? sideCommonDeleteTerms && !isLastSigningParticipant
                  : sellerSideDeleteTerms
                : false)),
          [isCurrentUser, isOwner, sideCommonDeleteTerms, sellerSideDeleteTerms]
        );
        const showStatuses = useMemo(
          () => !status.isDraft && !(isCurrentUser && !isSigned),
          [isCurrentUser, isSigned]
        );
        const showIdentifyMethodSelect =
          !status.isSigned && !isSigned && !isPublic && isBuyerSide;
        const showSignOrderBadge =
          isSignOrderUsed && status.isSigning && (isOwnSide || isTheirSide);
        const showSignOrderSelect = status.isDraft && (isOwnSide || isTheirSide);
        const isEmtyDeletePlace = isOwnSide || (isTheirSide && isBuyerSide);

        return (
          <div className={`${baseClass}-table-row`}>
            <div className={`${baseClass}-table-row-name`}>
              {showSignOrderBadge && (
                <span className={`${baseClass}-table-row-sign-order-badge`}>
                  {participant.signOrder || 1}
                </span>
              )}
              <span className="text-ellipsis">
                {getFullName(participant?.firstName, participant?.lastName)}
              </span>
              {isOwner && <IconCrown addClass="ml-1" />}
            </div>
            <div className={`${baseClass}-table-row-email ${partySide.toLowerCase()}`}>
              {participant.email ?? ""}
            </div>
            {isTheirSide && (
              <div className={`${baseClass}-table-row-company`}>
                <SignContractCompanyInfoTag
                  number={participant?.companyNumber}
                  name={participant?.companyName}
                  bordered={!!participant?.OrganizationMemberId}
                  numberAsTooltip={isTheirSide && status.isSigning}
                />
              </div>
            )}
            <div className={`${baseClass}-table-row-actions`}>
              {showStatuses && (
                <ParticipantStatusInfo
                  participant={participant}
                  onOpenIdentificationDetails={onOpenIdentificationDetails}
                />
              )}
              <LockValue
                canPermissions={["ENABLE_SIGN_CONTRACT_SIGNING_ORDER"]}
                needLabel
                additionalHideCondition={!showSignOrderSelect}
                justHide={!isSignOrderUsed || !showSignOrderSelect}
                noLockIcon
                needShowModalToSubscribe
              >
                <div className={`${baseClass}-table-row-order mr-4`}>
                  <MiniSelect
                    list={createArray(wholeParticipants.length).map((_, i) => ++i)}
                    disabled={!canUseHandysignSigningOrder}
                    selectedValue={participant.signOrder || 1}
                    onChange={(value) =>
                      onChange("signOrder", {
                        participantId: participant.id,
                        signOrder: value,
                      })
                    }
                  />
                </div>
              </LockValue>
              {(isOwnSide || isTheirSide) && (
                <div className={`${baseClass}-table-row-identify`}>
                  {showIdentifyMethodSelect ? (
                    <LockValue
                      canPermissions={["ENABLE_SIGN_CONTRACT_EXTRA_SIGN_IDENTIFY"]}
                      needLabel
                      justHide
                      addClassWrapper="ml-2"
                      needShowModalToSubscribe
                    >
                      {participant.identifyMethod ===
                        signContractIdentifyMethod.BASIC_AUTH && (
                        <Info
                          tooltip="SIGN_CONTRACT_BASIC_AUTH_TOOLTIP"
                          addClass="ml-1"
                        />
                      )}
                      <MiniSelect
                        list={identifyListForParticipant(participant)}
                        itemAsObject
                        borderColor={color.black}
                        selectedValue={participant.identifyMethod}
                        onChange={(value) =>
                          onChange("identifyMethod", {
                            id: participant.id,
                            identifyMethod: value,
                          })
                        }
                        addClass="ml-1"
                      />
                    </LockValue>
                  ) : (
                    <SignContractIdentifyMethodIconDisplay
                      method={participant.identifyMethod}
                      participant={participant}
                    />
                  )}
                </div>
              )}
              {status.isSigning && (
                <ParticipantActions
                  participant={participant}
                  isCurrentUser={isCurrentUser}
                />
              )}

              {isDeleteButton ? (
                <ButtonDelete
                  addClass="pr-0 ml-1"
                  onClick={() => handleDeleteContractParticipant(participant)}
                  activeOnHover
                />
              ) : (
                isEmtyDeletePlace && <span className="ml-6" />
              )}
            </div>
          </div>
        );
      }
    );

    const clickSaveCompany = () => {
      onChange(undefined, companyInfo, "update");
      setIsEditCompany(false);
    };

    useEffect(() => {
      setEdits({company: isEditCompany});
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditCompany]);

    return (
      <div className={cn(baseClass, {[addClass]: !!addClass})}>
        <div className={`df-row-center ${hasParticipants ? "mb-4" : "mb-2"}`}>
          <span className="__bold">{t(titleLabel)}</span>
          {isOwnSide && (
            <div className={`${baseClass}-title-company`}>
              {isEditCompany ? (
                <Input
                  tiny
                  flatView
                  width="98px"
                  customHeight="26"
                  placeholder={t("SIGN_CONTRACT_NUMBER_INPUT_PLACEHOLDER")}
                  value={companyInfo.companyNumber}
                  onChange={(e) => {
                    setCompanyInfo((prevState) => ({
                      ...prevState,
                      companyNumber: e.target.value,
                    }));
                  }}
                  addClass="pl-2 pr-2"
                />
              ) : (
                <span className="fz-12">
                  {formatCompanyNumber(contract?.companyNumber)}
                </span>
              )}
              <div className={`${baseClass}-title-company-divider`} />

              {isEditCompany ? (
                <Input
                  tiny
                  flatView
                  fullWidth
                  customHeight="26"
                  placeholder={t("SIGN_CONTRACT_NAME_INPUT_PLACEHOLDER")}
                  value={companyInfo.companyName}
                  onChange={(e) => {
                    setCompanyInfo((prevState) => ({
                      ...prevState,
                      companyName: e.target.value,
                    }));
                  }}
                  addClass="pl-2 pr-2"
                />
              ) : (
                <span className="fz-12">{contract?.companyName}</span>
              )}

              {status.isDraft && (
                <SignContractEditButton
                  edit={isEditCompany}
                  onEdit={() => setIsEditCompany(true)}
                  onSubmit={clickSaveCompany}
                />
              )}
            </div>
          )}

          {isAddTheirSideBuyerSideButton && !hasParticipants && (
            <AddTheirPartyMemberButton />
          )}
          {isOwnSideBuyerSideRightTitleContent && <RightTitleContent />}
        </div>

        {isTheirSide && hasParticipants && (
          <div className={`${baseClass}-table-head`}>
            <span className={`${baseClass}-table-head-name`}>
              {t(`${baseLangKey}SELLER_TABLE_HEAD_NAME`)}
            </span>
            <span className={`${baseClass}-table-head-email ${partySide.toLowerCase()}`}>
              {t(`${baseLangKey}SELLER_TABLE_HEAD_EMAIL`)}
            </span>
            <span className={`${baseClass}-table-head-company`}>
              {t(`${baseLangKey}SELLER_TABLE_HEAD_COMPANY`)}
            </span>
            <span className={`${baseClass}-table-head-actions`}></span>
          </div>
        )}

        {participants?.map((participant) => (
          <ParticipantRow
            key={participant.id}
            participant={participant}
            identifyMethodListForParticipant={identifyMethodListForParticipant}
            onOpenIdentificationDetails={openIdentificationDetails}
          />
        ))}

        {isAddOwnSideButton && (
          <MultipleSelect
            value={null}
            list={addableOrganizationMembers}
            small
            notShowingSelections
            noMultipleChange
            needSearch
            title={`${t(`${baseLangKey}BUYER_SELECTION`)} ${
              user?.ActiveOrganization?.name
            }`}
            titleInButton
            onChange={(value) =>
              onChange(signContractPartyMemberSide[partySide], value, "add")
            }
            addClass="mt-4 mb-2"
          />
        )}
        {isAddTheirSideBuyerSideButton && hasParticipants && (
          <AddTheirPartyMemberButton />
        )}
        {isAddTheirSideSellerSideButton && (
          <MultipleSelect
            value={null}
            list={addableOrganizationMembers}
            disabled={!hasAddableOrganizationMembers}
            disabledTooltip={`${baseLangKey}SELLER_SELECTION_DISABLED`}
            small
            notShowingSelections
            noMultipleChange
            needSearch
            title={`${baseLangKey}SELLER_SELECTION`}
            titleInButton
            onChange={(value) => onChange(signContractPartyMemberSide.OWN, value, "add")}
            addClass="mt-4 mb-2"
          />
        )}
        {(isAddOwnSideButton || isAddTheirSideSellerSideButton) && (
          <SignContractInviteLink addClass="mt-4 mb-2" />
        )}
        {identificationDetailsModal && (
          <SignContractIdentificationDetailsModal
            participant={identificationDetailsModal.participant}
            methodKind={identificationDetailsModal.methodKind}
            onClose={() => setIdentificationDetailsModal(null)}
          />
        )}
      </div>
    );
  }
);
